"use client";

import { c as _c } from "react-compiler-runtime";
import { motion } from "motion/react";
import type { OverlayPoint } from "./types";
const SCAN_REGIONS: Record<string, number[]> = {
  forehead: [10, 151, 9, 8, 168],
  rightEye: [33, 160, 159, 158, 133, 153, 144, 145, 246],
  leftEye: [362, 385, 386, 387, 263, 373, 374, 380, 466],
  nose: [168, 6, 197, 195, 5, 4, 1, 19],
  lips: [61, 185, 40, 39, 37, 0, 267, 269, 270, 409, 291, 375, 321, 405, 314, 17, 84, 181, 91, 146],
  chin: [152, 377, 400, 378, 379, 365, 397, 288, 361, 323, 454, 356, 389, 251, 284, 332, 297, 338, 10],
  jawlineRight: [172, 136, 150, 149, 176, 148, 152],
  jawlineLeft: [152, 377, 400, 378, 379, 365, 397],
  rightCheek: [116, 123, 147, 213, 192],
  leftCheek: [345, 352, 376, 433, 416]
};
const SCAN_SEQUENCE = [{
  regions: ["forehead"],
  label: "Forehead"
}, {
  regions: ["rightEye", "leftEye"],
  label: "Eyes"
}, {
  regions: ["nose"],
  label: "Nose"
}, {
  regions: ["rightCheek", "leftCheek"],
  label: "Cheeks"
}, {
  regions: ["lips"],
  label: "Lips"
}, {
  regions: ["chin", "jawlineRight", "jawlineLeft"],
  label: "Jawline"
}];
interface RegionBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  centerX: number;
  centerY: number;
}
type FaceScanOverlayProps = {
  landmarks: OverlayPoint[] | null;
  isActive: boolean;
  currentStep: number;
};
export default function FaceScanOverlay(t0) {
  const $ = _c(63);
  const {
    landmarks,
    isActive,
    currentStep
  } = t0;
  if (!isActive || !landmarks || landmarks.length === 0) {
    return null;
  }
  const sequenceIndex = currentStep % SCAN_SEQUENCE.length;
  const currentScanStep = SCAN_SEQUENCE[sequenceIndex];
  const activeRegions = currentScanStep.regions;
  let chinBounds;
  let leftEyeBounds;
  let noseBounds;
  let rightEyeBounds;
  let showEyeConnection;
  let showNoseConnection;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== activeRegions || $[1] !== landmarks) {
    let t5;
    if ($[12] !== landmarks) {
      t5 = regionName => {
        const indices = SCAN_REGIONS[regionName];
        return getRegionBounds(landmarks, indices);
      };
      $[12] = landmarks;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    const regionBounds = activeRegions.map(t5);
    let t6;
    if ($[14] !== landmarks) {
      t6 = getRegionBounds(landmarks, [...SCAN_REGIONS.forehead, ...SCAN_REGIONS.chin, ...SCAN_REGIONS.rightCheek, ...SCAN_REGIONS.leftCheek]);
      $[14] = landmarks;
      $[15] = t6;
    } else {
      t6 = $[15];
    }
    const faceBounds = t6;
    let t7;
    if ($[16] !== landmarks) {
      t7 = getRegionBounds(landmarks, SCAN_REGIONS.leftEye);
      $[16] = landmarks;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    leftEyeBounds = t7;
    let t8;
    if ($[18] !== landmarks) {
      t8 = getRegionBounds(landmarks, SCAN_REGIONS.rightEye);
      $[18] = landmarks;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    rightEyeBounds = t8;
    let t9;
    if ($[20] !== landmarks) {
      t9 = getRegionBounds(landmarks, SCAN_REGIONS.nose);
      $[20] = landmarks;
      $[21] = t9;
    } else {
      t9 = $[21];
    }
    noseBounds = t9;
    let t10;
    if ($[22] !== landmarks) {
      t10 = getRegionBounds(landmarks, SCAN_REGIONS.chin);
      $[22] = landmarks;
      $[23] = t10;
    } else {
      t10 = $[23];
    }
    chinBounds = t10;
    let t11;
    if ($[24] !== activeRegions) {
      t11 = activeRegions.includes("leftEye") || activeRegions.includes("rightEye");
      $[24] = activeRegions;
      $[25] = t11;
    } else {
      t11 = $[25];
    }
    showEyeConnection = t11;
    let t12;
    if ($[26] !== activeRegions) {
      t12 = activeRegions.includes("nose") || activeRegions.includes("chin");
      $[26] = activeRegions;
      $[27] = t12;
    } else {
      t12 = $[27];
    }
    showNoseConnection = t12;
    t1 = "pointer-events-none absolute inset-0 overflow-hidden";
    let t13;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = {
        left: ["0%", "100%", "0%"]
      };
      $[28] = t13;
    } else {
      t13 = $[28];
    }
    const t14 = `${faceBounds.centerX * 100}%`;
    let t15;
    if ($[29] !== t14) {
      t15 = {
        left: t14
      };
      $[29] = t14;
      $[30] = t15;
    } else {
      t15 = $[30];
    }
    let t16;
    if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = {
        duration: 2.5,
        repeat: Infinity,
        ease: "easeInOut"
      };
      $[31] = t16;
    } else {
      t16 = $[31];
    }
    if ($[32] !== t15) {
      t2 = <motion.div animate={t13} className="via-primary/60 absolute top-0 bottom-0 w-[2px] bg-gradient-to-b from-transparent to-transparent" style={t15} transition={t16} />;
      $[32] = t15;
      $[33] = t2;
    } else {
      t2 = $[33];
    }
    let t17;
    if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = {
        top: ["0%", "100%", "0%"]
      };
      $[34] = t17;
    } else {
      t17 = $[34];
    }
    if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <motion.div animate={t17} className="via-primary/60 absolute right-0 left-0 h-[2px] bg-gradient-to-r from-transparent to-transparent" transition={{
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut"
      }} />;
      $[35] = t3;
    } else {
      t3 = $[35];
    }
    let t18;
    if ($[36] !== activeRegions) {
      t18 = (bounds, idx) => <motion.div animate={{
        scale: [1, 1.1, 1],
        opacity: [0.6, 0.9, 0.6]
      }} className="absolute rounded-full" key={`region-${activeRegions[idx]}`} style={{
        left: `${bounds.centerX * 100}%`,
        top: `${bounds.centerY * 100}%`,
        width: `${(bounds.maxX - bounds.minX) * 100}%`,
        height: `${(bounds.maxY - bounds.minY) * 100}%`,
        transform: "translate(-50%, -50%)",
        background: "radial-gradient(circle, rgba(var(--primary-rgb) / 0.3) 0%, transparent 70%)"
      }} transition={{
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut"
      }} />;
      $[36] = activeRegions;
      $[37] = t18;
    } else {
      t18 = $[37];
    }
    t4 = regionBounds.map(t18);
    $[0] = activeRegions;
    $[1] = landmarks;
    $[2] = chinBounds;
    $[3] = leftEyeBounds;
    $[4] = noseBounds;
    $[5] = rightEyeBounds;
    $[6] = showEyeConnection;
    $[7] = showNoseConnection;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    chinBounds = $[2];
    leftEyeBounds = $[3];
    noseBounds = $[4];
    rightEyeBounds = $[5];
    showEyeConnection = $[6];
    showNoseConnection = $[7];
    t1 = $[8];
    t2 = $[9];
    t3 = $[10];
    t4 = $[11];
  }
  let t5;
  if ($[38] !== leftEyeBounds || $[39] !== rightEyeBounds || $[40] !== showEyeConnection) {
    t5 = showEyeConnection ? <motion.line animate={{
      pathLength: [0, 1]
    }} stroke="hsl(var(--primary))" strokeDasharray="4 4" strokeWidth="1" transition={{
      duration: 1.5,
      repeat: Infinity,
      ease: "linear"
    }} x1={`${leftEyeBounds.centerX * 100}%`} x2={`${rightEyeBounds.centerX * 100}%`} y1={`${leftEyeBounds.centerY * 100}%`} y2={`${rightEyeBounds.centerY * 100}%`} /> : null;
    $[38] = leftEyeBounds;
    $[39] = rightEyeBounds;
    $[40] = showEyeConnection;
    $[41] = t5;
  } else {
    t5 = $[41];
  }
  let t6;
  if ($[42] !== chinBounds || $[43] !== noseBounds || $[44] !== showNoseConnection) {
    t6 = showNoseConnection ? <motion.line animate={{
      pathLength: [0, 1]
    }} stroke="hsl(var(--primary))" strokeDasharray="4 4" strokeWidth="1" transition={{
      duration: 1.5,
      repeat: Infinity,
      ease: "linear"
    }} x1={`${noseBounds.centerX * 100}%`} x2={`${chinBounds.centerX * 100}%`} y1={`${noseBounds.centerY * 100}%`} y2={`${chinBounds.centerY * 100}%`} /> : null;
    $[42] = chinBounds;
    $[43] = noseBounds;
    $[44] = showNoseConnection;
    $[45] = t6;
  } else {
    t6 = $[45];
  }
  let t7;
  if ($[46] !== t5 || $[47] !== t6) {
    t7 = <svg className="absolute inset-0 h-full w-full" xmlns="http://www.w3.org/2000/svg">{t5}{t6}</svg>;
    $[46] = t5;
    $[47] = t6;
    $[48] = t7;
  } else {
    t7 = $[48];
  }
  let t8;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      opacity: [0.3, 1, 0.3]
    };
    $[49] = t8;
  } else {
    t8 = $[49];
  }
  let t9;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <motion.div animate={t8} className="border-primary absolute top-0 left-0 h-6 w-6 border-t-2 border-l-2" transition={{
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut",
      delay: 0
    }} />;
    $[50] = t9;
  } else {
    t9 = $[50];
  }
  let t10;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      opacity: [0.3, 1, 0.3]
    };
    $[51] = t10;
  } else {
    t10 = $[51];
  }
  let t11;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <motion.div animate={t10} className="border-primary absolute top-0 right-0 h-6 w-6 border-t-2 border-r-2" transition={{
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut",
      delay: 0.5
    }} />;
    $[52] = t11;
  } else {
    t11 = $[52];
  }
  let t12;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      opacity: [0.3, 1, 0.3]
    };
    $[53] = t12;
  } else {
    t12 = $[53];
  }
  let t13;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <motion.div animate={t12} className="border-primary absolute bottom-0 left-0 h-6 w-6 border-b-2 border-l-2" transition={{
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut",
      delay: 1
    }} />;
    $[54] = t13;
  } else {
    t13 = $[54];
  }
  let t14;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      opacity: [0.3, 1, 0.3]
    };
    $[55] = t14;
  } else {
    t14 = $[55];
  }
  let t15;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <motion.div animate={t14} className="border-primary absolute right-0 bottom-0 h-6 w-6 border-r-2 border-b-2" transition={{
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut",
      delay: 1.5
    }} />;
    $[56] = t15;
  } else {
    t15 = $[56];
  }
  let t16;
  if ($[57] !== t1 || $[58] !== t2 || $[59] !== t3 || $[60] !== t4 || $[61] !== t7) {
    t16 = <div className={t1}>{t2}{t3}{t4}{t7}{t9}{t11}{t13}{t15}</div>;
    $[57] = t1;
    $[58] = t2;
    $[59] = t3;
    $[60] = t4;
    $[61] = t7;
    $[62] = t16;
  } else {
    t16 = $[62];
  }
  return t16;
}
function getRegionBounds(landmarks: OverlayPoint[], indices: number[]): RegionBounds {
  let minX = 1;
  let maxX = 0;
  let minY = 1;
  let maxY = 0;
  for (const idx of indices) {
    const landmark = landmarks[idx];
    if (landmark.x < minX) minX = landmark.x;
    if (landmark.x > maxX) maxX = landmark.x;
    if (landmark.y < minY) minY = landmark.y;
    if (landmark.y > maxY) maxY = landmark.y;
  }
  return {
    minX,
    maxX,
    minY,
    maxY,
    centerX: (minX + maxX) / 2,
    centerY: (minY + maxY) / 2
  };
}